import { useIsAmbientCopilotEnabled } from '@/app-shell/flags/agents-flags';
import { useIsScribeBlockEditorCodeblockEnabled } from '@/app-shell/flags/session-flags';
import { CdiNudgeHighlights } from '@/components/tiptap/shared/cdi-nudge-highlights/extension';
import { MedicationHighlights } from '@/components/tiptap/shared/medication-highlights/extension';
import { toNoteEditorSurface } from '@/components/tiptap/telemetry/emit-note-editor-render-failed';
import { useIsSectionsInNotesEnabledForEditor } from '@/hooks/useMarkdownSectionalEditorFlags';
import {
  useEditorLifecycleTelemetry,
  type EditorTelemetrySurface,
} from '@/utils/telemetry/editor-observability';
import { CodingOverlay } from '@heidi/coding';
import { withCanvasDelayedDictateHint } from '@heidi/dictate';
import {
  configuredDropcursor,
  configuredTypography,
  createHorizontalRuleNodeView,
} from '@heidi/editor/core';
import { useEditorContentSync, useEditorEditableSync } from '@heidi/editor/react';
import { useIsProblemBasedChartingEnabled } from '@heidi/integrations/flags';
import { useTranslations } from '@heidi/runtime/i18n';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import Gapcursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Italic from '@tiptap/extension-italic';
import { ListItem, OrderedList } from '@tiptap/extension-list';
import NodeRange from '@tiptap/extension-node-range';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder, { type PlaceholderOptions } from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Text from '@tiptap/extension-text';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { textblockTypeInputRule, useEditor, type Extension } from '@tiptap/react';
import { useMemo } from 'react';
import { Dictation } from '../sharedExtensions/dictation/dictation';
import { DictationCursor } from '../sharedExtensions/dictation/dictation-cursor';
import { NoteSuggestionHighlight } from '../sharedExtensions/note-suggestion-highlight';
import { EmphasisShortcutGate } from '../sharedExtensions/shortcuts/emphasis-shortcut-gate';
import { EscapeToFocusOut } from '../sharedExtensions/shortcuts/escape-to-focus-out';
import { KeydownSelectionGuard } from '../sharedExtensions/shortcuts/keydown-selection-guard';
import { ShimmerExtension } from '../v1/extensions/text-shimmer/text-shimmer';
import { ClipboardTextSerializer } from './extensions/clipboard-text-serializer';
import { DiffDecorationsExtension } from './extensions/diff-decorations';
import { EntityVerificationHighlight } from './extensions/entity-verification-highlight';
import { NoteEvidenceHighlight } from './extensions/note-evidence-highlight';
import { SectionNode } from './extensions/section-node';
import { SlashCommand } from './extensions/slash-command';
import { resolveSectionsEnabled } from './resolveSectionsEnabled';

export const useGetExtensions = ({
  placeholder,
  excludedExtensionNames,
  showPlaceholderWhenDisabled = false,
  isTemplateEditor = false,
}: {
  placeholder: PlaceholderOptions['placeholder'];
  excludedExtensionNames: string[];
  showPlaceholderWhenDisabled?: boolean;
  isTemplateEditor?: boolean;
}) => {
  const enableProblemBasedCharting = useIsProblemBasedChartingEnabled();

  const extensions = useMemo(() => {
    const listExtensions = enableProblemBasedCharting
      ? [BulletList, OrderedList, ListItem]
      : [];
    const codingOverlayExtensions = enableProblemBasedCharting
      ? [
          CodingOverlay.configure({
            problems: [],
          }),
        ]
      : [];

    return [
      ShimmerExtension,
      Document,
      ...listExtensions,
      Gapcursor,
      Text,
      NoteEvidenceHighlight,
      EntityVerificationHighlight,
      MedicationHighlights,
      CdiNudgeHighlights,
      NoteSuggestionHighlight,
      DiffDecorationsExtension,
      Placeholder.configure({
        showOnlyWhenEditable: !showPlaceholderWhenDisabled,
        placeholder: withCanvasDelayedDictateHint(placeholder),
        showOnlyCurrent: false,
      }),
      Paragraph,
      Heading.configure({ levels: [1] }).extend({
        addInputRules() {
          return [];
        },
        addKeyboardShortcuts() {
          return {};
        },
      }),
      Bold,
      Italic,
      Subscript,
      HardBreak,
      NodeRange.configure({
        key: 'Shift',
      }),
      Dictation,
      DictationCursor,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      SlashCommand,
      History,
      EscapeToFocusOut,
      KeydownSelectionGuard,
      EmphasisShortcutGate,
      ClipboardTextSerializer.configure({
        copySource: isTemplateEditor ? 'template_editor' : 'editor_clipboard',
      }),
      configuredTypography,
      HorizontalRule.extend({
        addNodeView() {
          return createHorizontalRuleNodeView(this.name);
        },
      }),
      CodeBlock.extend({
        // Deliberately replace (not augment) parent input rules so the
        // tilde-fence (`~~~`) trigger is dropped — only the backtick fence
        // is supported. Language regex accepts any non-whitespace, non-backtick
        // character so json5, c++, c# etc. trigger the input rule.
        addInputRules() {
          return [
            textblockTypeInputRule({
              find: /^```([^\s`]+)?[\s\n]$/,
              type: this.type,
              getAttributes: (match: RegExpMatchArray) => ({
                language: match[1],
              }),
            }),
          ];
        },
      }),
      configuredDropcursor,
      Table.extend({
        draggable: true,
      }).configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      SectionNode,
      Underline,
      ...codingOverlayExtensions,
    ];
  }, [
    placeholder,
    showPlaceholderWhenDisabled,
    enableProblemBasedCharting,
    isTemplateEditor,
  ]);

  return useMemo(
    () =>
      extensions.filter(
        (ext) => !excludedExtensionNames.some((excluded) => excluded === ext.name)
      ),
    [extensions, excludedExtensionNames]
  );
};

/**
 * The DOM attributes ProseMirror puts on the editing host.
 *
 * Exported so the a11y contract is pinned by a PR-time test rather than by a
 * `blocking: false`, dispatch-only lane — a later edit here (or a caller
 * passing its own `attributes` through `editorPropsOverride`, which replaces
 * these wholesale) would otherwise drop the accessible name silently.
 *
 * `aria-readonly` tracks `editable`, because `role="textbox"` overrides the
 * native mapping whether or not there is any editing to do: without it, a user
 * with no `SESSION_EDIT` gets `contenteditable="false"` announced as an
 * editable multiline field — worse than the unlabelled generic content it
 * replaced, for that one cohort.
 *
 * Known limit, and a deliberate trade: these are fixed at construction, the
 * same as `placeholder` and for the same reason (adding them to `useEditor`'s
 * deps would recreate the editor and drop cursor and undo state), while
 * `useEditorEditableSync` can flip `editable` afterwards. So this is the
 * initial permission, and it goes stale on a mid-session permission change —
 * which does not realistically happen.
 */
export function editingHostAttributes({
  ariaLabel,
  editable,
}: {
  ariaLabel?: string;
  editable: boolean;
}): Record<string, string> {
  if (!ariaLabel) return { class: 'flex-1' };
  return {
    class: 'flex-1',
    role: 'textbox',
    'aria-multiline': 'true',
    'aria-label': ariaLabel,
    ...(editable ? {} : { 'aria-readonly': 'true' }),
  };
}

export const useBlockEditorMarkdown = ({
  content,
  editable,
  placeholder,
  excludedExtensions = [],
  includedExtensions = [],
  autofocus = false,
  showPlaceholderWhenDisabled = false,
  isStreaming = false,
  editorPropsOverride,
  ariaLabel,
  enableSections = false,
  suppressContentSyncEchoWhileFocused = false,
  surface,
  isTemplateEditor = false,
}: {
  content: string;
  editable: boolean;
  /** Static string or function for dynamic placeholder updates */
  placeholder?: PlaceholderOptions['placeholder'];
  excludedExtensions?: Extension[];
  includedExtensions?: Extension[];
  autofocus?: boolean;
  showPlaceholderWhenDisabled?: boolean;
  /** When true, content sync is throttled to reduce editor thrashing during SSE streaming */
  isStreaming?: boolean;
  editorPropsOverride?: Record<string, unknown>;
  /**
   * Name and role the editing host publishes to assistive technology.
   *
   * A bare `contenteditable` has neither, and the platforms disagree about
   * what it even is: WebKit publishes it as a text area, Chromium as a generic
   * container with no editable role at all — so on Windows a screen reader
   * reaches unlabelled generic content. Setting this makes it a labelled
   * multiline textbox on both, which is the ARIA shape for a rich text editor.
   */
  ariaLabel?: string;
  /** When true, opts this editor instance into section-aware editing (SectionNode + Heading extensions). */
  enableSections?: boolean;
  /** When true, skip the post-save content-sync echo while the editor is focused (see useEditorContentSync). */
  suppressContentSyncEchoWhileFocused?: boolean;
  /** Lifecycle + note-tab SLI membership. `document` is lifecycle-only. */
  surface?: EditorTelemetrySurface;
  /** When true, ⌘C / cut / context-menu copies emit `copy_source: template_editor`. */
  isTemplateEditor?: boolean;
}) => {
  const enableSectionParser = useIsSectionsInNotesEnabledForEditor();
  const ambientCopilotEnabled = useIsAmbientCopilotEnabled();
  const enableProblemBasedCharting = useIsProblemBasedChartingEnabled();
  const codeBlockEnabled = useIsScribeBlockEditorCodeblockEnabled();

  const { t } = useTranslations();
  const defaultPlaceholder = t('common.clickHereToStartWriting');
  const finalPlaceholder = placeholder ?? defaultPlaceholder;

  // The ambient-copilot cohort forces sections on even when the base release flag
  // is off, so the note can be split into fields to map into the EHR.
  //
  // `sectionsEnabled` feeds the `useEditor` deps below, so a late false -> true
  // flip recreates the editor and drops cursor/undo state. Only reachable for
  // the Athena markdown cohort on a surface that hasn't already warmed the
  // onboarding-sessions cache (template editors, signup preview): the query
  // starts at mount, so the window is a single round-trip before the user can
  // realistically type. Everyone else resolves synchronously from flag reads.
  const sectionsEnabled = resolveSectionsEnabled({
    enableSections,
    enableSectionParser,
    ambientCopilotEnabled,
  });

  const excludedExtensionNames = useMemo(() => {
    return [
      ...excludedExtensions.map((ext) => ext.name),
      ...(!sectionsEnabled ? [SectionNode.name, Heading.name] : []),
      ...(!codeBlockEnabled ? [CodeBlock.name] : []),
    ];
  }, [excludedExtensions, sectionsEnabled, codeBlockEnabled]);

  const baseExtensions = useGetExtensions({
    placeholder: finalPlaceholder,
    excludedExtensionNames,
    showPlaceholderWhenDisabled,
    isTemplateEditor,
  });

  const extensions = useMemo(
    () => [...baseExtensions, ...includedExtensions],
    [baseExtensions, includedExtensions]
  );

  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      autofocus,
      extensions,
      editorProps: {
        attributes: editingHostAttributes({ ariaLabel, editable }),
        // Note this spread replaces `attributes` wholesale when a caller
        // supplies its own — the merge above is within the default only.
        ...editorPropsOverride,
      },
      editable,
      content,
    },
    [sectionsEnabled, enableProblemBasedCharting, codeBlockEnabled]
  );

  useEditorLifecycleTelemetry({
    editor,
    variant: 'markdown',
    surface: surface ?? 'note',
  });
  useEditorContentSync({
    editor,
    content,
    editorVariant: 'block_markdown',
    surface: toNoteEditorSurface(surface),
    isStreaming,
    suppressEchoWhileFocused: suppressContentSyncEchoWhileFocused,
  });
  useEditorEditableSync(editor, editable);

  return { editor };
};
